import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, isApiError, type SlideImportJob, type SlideImportSource, type StagedDeck } from '../lib/api'
import { bidJobsKey } from '../lib/bid-jobs'
import { isTransientError } from '../lib/errors'
import { fileToBase64 } from '../lib/files'
import { MAX_LIBRARY_TEMPLATES, officeTemplatesQuery, refreshOfficeTemplates } from '../lib/office-templates'
import { isTemplateLibrary } from '../lib/template-library'
import { deckQuery } from '../slides/template-query'
import {
  DEFAULT_MODE,
  DIALOG_MODES,
  MAX_IMPORT_SLIDES,
  MAX_TEMPLATE_NAME,
  PPTX_ACCEPT,
  chosenTarget,
  deckTemplateName,
  defaultSelection,
  hasHiddenSlides,
  importModeOf,
  inFlight,
  isEditableMode,
  jobUnreadable,
  keptTemplateWords,
  labelWarning,
  libraryChoices,
  modeUnavailable,
  orderedSelection,
  overCap,
  refusalWords,
  selectableSlides,
  setPendingReport,
  stepWords,
  withHiddenSlides,
  type DialogMode,
  type ImportPanel,
  type Words,
} from '../lib/slide-import'
import { ArtefactTypeGlyph } from './ArtefactTypeIcon'
import { ImportPanelButtons } from './ImportPanelButtons'
import { KeptTemplateLine } from './KeptTemplateLine'
import { SharePointBrowser, spbHeightForRows } from './SharePointBrowser'
import { TemplateMasterSelects } from './TemplateMasterSelects'

/** Where the imported slides go: a slides part (its room key) and the 0-based position in its deck. */
export interface ImportTarget {
  room: string
  /** Absent = the end of the deck. */
  at?: number
}

/** How often the running job is read again. */
const POLL_MS = 1000
/** The bid site's browser shows this many rows before it scrolls. */
const BROWSER_ROWS = 7

/** Each mode's label and hint (the radio's accessible description). */
const MODE_WORDS: Record<DialogMode, { label: Key; hint: Key }> = {
  template: { label: 'pptx_mode_template', hint: 'pptx_mode_template_hint' },
  source_new: { label: 'pptx_mode_source_new', hint: 'pptx_mode_source_new_hint' },
  source_merge: { label: 'pptx_mode_source_merge', hint: 'pptx_mode_source_merge_hint' },
  pictures: { label: 'pptx_mode_pictures', hint: 'pptx_mode_pictures_hint' },
}

/**
 * "Import PowerPoint" (plans/PPTX_IMPORT.md I2, I4, I8 — AB#442, AB#446, AB#455): a .pptx — the whole
 * deck or chosen slides — into a slides part, through the backend import (routes/slide-imports.ts):
 * converted into editable slides on a template of the bid (the default) or on the deck's own masters
 * (keeping its source formatting: in a new template, or merged into one), or as pictures.
 * Nothing runs on the member's machine: the backend vets the deck, converts it, and renders it as
 * Office draws it.
 *
 * Three steps in one dialog:
 * 1. **The deck** — a file from disk (the picker, or dropped on the zone), a file of the bid's
 *    SharePoint site (the existing {@link SharePointBrowser}, pinned to the site) or a pasted
 *    SharePoint link. It is staged (`POST /v1/slides/imports/staged`): an encrypted deck, a file
 *    that is not a presentation, one with macros or one too large is refused here, in words.
 * 2. **The mode and the slides** — "On a template of this bid" (editable, the default: a Template
 *    select and, when that template has several masters, a Master select, AB#482, which start on the
 *    deck's own and are sent as the import's target), "Keep source formatting" (editable; AB#483) as a
 *    new template (a name, starting as the deck's; disabled, saying why, in a full library) or merged
 *    into a template (a Template select, starting on the deck's) — both a 16:9 deck only, disabled,
 *    saying why, for another; each sent as the import's destination —, or "As pictures". The slides by number and title (no thumbnails before the renders): checkboxes, all /
 *    none, every visible slide checked to start with. A hidden slide is listed greyed and cannot be
 *    picked — as a picture never (Graph's PDF leaves it out); in the editable modes, "Include hidden
 *    slides" (shown when the deck has some) makes them pickable and checks them. At most 50: a
 *    counter, and a message beyond, Import disabled. The label warning when the deck is not Sec2.
 * 3. **The job** — `target()` resolves where the slides go (the editor's part, or a slides artefact
 *    created right then by New artefact › From PowerPoint — once, a second Import reuses it), the
 *    import starts (202) and the job is read every second: the step, its count (12 / 48, none when
 *    there is nothing to count) and a bar for the whole job (the backend's percentage, the one the
 *    bid home's Jobs section shows) that never moves back; a retry starts them over on what is left.
 *    Done: `onJobEnd`, at once, whatever the dialog does next — keeping the source formatting, the
 *    bid's Office templates are read again first (one was created or changed). A picture import has
 *    nothing to offer: `onDone`, and the caller closes the dialog and lands on the first imported
 *    slide. An editable one stays (AB#462), saying Completed with "Import report" and "Compare with
 *    original" — each asks the editor for that panel (lib/slide-import.ts), then `onDone` — and
 *    Close, which is `onDone` too; keeping the source formatting, it also says what became of the
 *    masters, with a link to the template (AB#483). Locked or failed: Retry; failed renders: "Retry renders" (both
 *    `POST …/:id/renders/retry`). The reads go on until the job settles, whatever fails on the way;
 *    only a job that can no longer be read at all (gone, access refused) stops them, in words.
 *
 * Closing while the job runs is allowed — it goes on server-side, the slides land anyway, and the
 * job is in the bid home's Jobs section; nothing reopens. Closing is refused only while the target
 * is being created and the import started (no orphan half-way).
 * Focus moves into the dialog, is trapped there, follows each step, and returns to the opener.
 */
export function ImportPowerPointDialog({
  bid,
  room,
  target,
  intro,
  onJobEnd,
  onDone,
  onClose,
  pollMs = POLL_MS,
}: {
  /** The bid the deck is imported into: its SharePoint site is the one browsed. */
  bid: string
  /** The part the slides go to, when it exists yet: its deck's templates are offered, else the bid's library. */
  room?: string
  /** Where the slides go, resolved when the member confirms the import (called once). */
  target: () => Promise<ImportTarget>
  /** A line under the title, saying where the slides go when that is not the open deck. */
  intro?: string
  /** The job is done, the dialog still open or not: the work that cannot wait for it to close. */
  onJobEnd?: (job: SlideImportJob) => void
  /** The dialog ends on a job done (see above): the caller closes it and lands on the imported slides. */
  onDone: (job: SlideImportJob) => void
  /** The dialog is closed with no job done. */
  onClose: () => void
  /** The job's polling interval (a test seam). */
  pollMs?: number
}) {
  const { t, lang } = useLang()
  const queryClient = useQueryClient()
  const ids = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const pickRef = useRef<HTMLButtonElement>(null)
  const stepRef = useRef<HTMLHeadingElement>(null)

  const [dropping, setDropping] = useState(false)
  const [browsing, setBrowsing] = useState(false)
  const [link, setLink] = useState('')
  const [staging, setStaging] = useState<string | null>(null)
  const [deck, setDeck] = useState<StagedDeck | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [mode, setMode] = useState<DialogMode>(DEFAULT_MODE)
  /** "Include hidden slides" (the editable modes only): hidden slides become pickable. */
  const [includeHidden, setIncludeHidden] = useState(false)
  /** The template and master the member picked; null = the deck's own. */
  const [pickedTemplate, setPickedTemplate] = useState<string | null>(null)
  const [pickedMaster, setPickedMaster] = useState<number | null>(null)
  /** The new template's name as the member typed it; null = the deck's. */
  const [typedName, setTypedName] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [importId, setImportId] = useState<string | null>(null)
  const [job, setJob] = useState<SlideImportJob | null>(null)
  /** The whole job, 0–100, never moving back; null until the backend says how far it is (the bar then has no value). */
  const [bar, setBar] = useState<number | null>(null)
  /** The job can no longer be read at all: the reads stopped. */
  const [lost, setLost] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** What `target()` resolved: a second Import (after a refused start) must not create another artefact. */
  const resolved = useRef<ImportTarget | null>(null)
  // The synchronous guard: React state alone lets a second click land before the re-render.
  const busyRef = useRef(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // The template library has no SharePoint site (and answers 404 to the lookup), as in ExportMenu.
  const site = useQuery({ queryKey: ['bid-site', bid], queryFn: () => api.bidSite(bid), enabled: !isTemplateLibrary(bid) })
  const bidSite = site.data?.site ?? null

  // What the first mode can land on. Read again on opening (stale for up to 5 minutes otherwise): a
  // choice of the moment. Failing or still on its way, the import goes on the deck's default as ever.
  const directory = useQuery({ ...deckQuery(bid, room ?? ''), enabled: !!room, staleTime: 0 })
  const library = useQuery({ ...officeTemplatesQuery(bid, 'slides'), enabled: !room })
  const choices = room ? (directory.data ?? null) : library.data ? libraryChoices(library.data) : null
  const choicesFailed = room ? directory.isError : library.isError
  const landing = chosenTarget(choices, pickedTemplate, pickedMaster)
  const landingMasters = choices?.templates.find((x) => x.id === landing?.templateId)?.masters ?? []
  const templateCount = choices?.templates.length
  const templateOptions = (choices?.templates ?? []).map((x) => ({ value: x.id, label: x.name }))

  const phase: 'source' | 'staging' | 'pick' | 'job' = importId ? 'job' : deck ? 'pick' : staging !== null ? 'staging' : 'source'

  // Focus in on open, back to the opener on close; then onto each step as it comes.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    return () => prev?.focus()
  }, [])
  useEffect(() => {
    if (phase === 'source') pickRef.current?.focus()
    else if (phase === 'staging') dialogRef.current?.focus()
    else stepRef.current?.focus()
  }, [phase])

  /** A refusal in words: the app's own when the backend named it, else the backend's, else a generic one. */
  const say = (e: unknown, step: 'stage' | 'start' | 'retry'): string => {
    const words = refusalWords(e, step, lang, mode)
    if (words) return fill(t(words.key), words)
    if (isApiError(e) && e.serverMessage) return e.serverMessage
    return t(isTransientError(e) ? 'pptx_err_unreachable' : 'prompt_failed')
  }

  // ── 1. the deck ──────────────────────────────────────────────────────────────────────────────
  /** Stage a deck; a file from disk is still being read when this starts (a large deck takes a moment). */
  const stage = (source: SlideImportSource | Promise<SlideImportSource>, name: string): void => {
    if (staging !== null) return
    setStaging(name)
    setError(null)
    Promise.resolve(source)
      .then((src) => api.stageSlideImport(src))
      .then(
        (staged) => {
          if (!mountedRef.current) return
          setStaging(null)
          setBrowsing(false)
          setDeck(staged)
          setSelected(defaultSelection(staged))
          setIncludeHidden(false)
          setTypedName(null)
          // A 4:3 deck cannot keep its source formatting: back to the default mode.
          setMode((m) => (modeUnavailable(staged, m, templateCount) ? DEFAULT_MODE : m))
        },
        (e: unknown) => {
          if (!mountedRef.current) return
          setStaging(null)
          setError(say(e, 'stage'))
        },
      )
  }
  const takeFile = (files: FileList | null): void => {
    const file = files?.[0]
    if (file) stage(fileToBase64(file).then((base64) => ({ base64, name: file.name })), file.name)
  }
  /** A file dragged anywhere over the dialog: taken on the deck step, never opened by the browser. */
  const dragsFiles = (e: DragEvent): boolean => e.dataTransfer.types.includes('Files')
  const openLink = (): void => {
    const url = link.trim()
    if (url) stage({ sharepoint: { url } }, url)
  }

  // ── 2. the mode and the slides ───────────────────────────────────────────────────────────────
  /** Hidden slides can be picked: the editable modes, on request. */
  const hiddenOn = isEditableMode(mode) && includeHidden
  const pickable = deck ? selectableSlides(deck, hiddenOn) : []
  const extra = overCap(selected)
  /** A picture import cannot take a hidden slide: leaving the editable mode leaves them out. */
  const chooseMode = (next: DialogMode): void => {
    setMode(next)
    if (next === 'pictures' && deck && includeHidden) {
      setIncludeHidden(false)
      setSelected((prev) => withHiddenSlides(deck, prev, false))
    }
  }
  const toggleHidden = (include: boolean): void => {
    if (!deck) return
    setIncludeHidden(include)
    setSelected((prev) => withHiddenSlides(deck, prev, include))
  }
  const toggle = (index: number): void =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (!next.delete(index)) next.add(index)
      return next
    })
  const warning = deck ? labelWarning(deck) : null
  const newName = typedName ?? (deck ? deckTemplateName(deck.name) : '')
  const canImport = !!deck && selected.size > 0 && extra === 0 && !starting && !modeUnavailable(deck, mode, templateCount) && (mode !== 'source_merge' || !!landing)
  const otherDeck = (): void => {
    setDeck(null)
    setSelected(new Set())
    setError(null)
  }

  // ── 3. the job ───────────────────────────────────────────────────────────────────────────────
  const start = (): void => {
    if (busyRef.current || !deck || !canImport) return
    busyRef.current = true
    setStarting(true)
    setError(null)
    const run = async (): Promise<string> => {
      resolved.current ??= await target()
      const { room, at } = resolved.current
      const res = await api.startSlideImport({
        stagedId: deck.stagedId,
        room,
        ...(at !== undefined ? { at } : {}),
        mode: importModeOf(mode),
        slides: orderedSelection(selected),
        ...(hiddenOn ? { includeHidden: true } : {}),
        ...(mode === 'template' && landing ? { target: landing } : {}),
        ...(mode === 'source_new' ? { destination: { type: 'new' as const, ...(newName.trim() ? { name: newName.trim() } : {}) } } : {}),
        ...(mode === 'source_merge' && landing ? { destination: { type: 'merge' as const, templateId: landing.templateId } } : {}),
      })
      return res.importId
    }
    run().then(
      (id) => {
        busyRef.current = false
        // A job the section can show at once (the dialog may be closed before it ends).
        void queryClient.invalidateQueries({ queryKey: bidJobsKey(bid) })
        if (!mountedRef.current) return
        setStarting(false)
        setImportId(id)
      },
      (e: unknown) => {
        busyRef.current = false
        if (!mountedRef.current) return
        setStarting(false)
        setError(say(e, 'start'))
      },
    )
  }

  // Read the job until it settles: after the start, after a retry, and again after every read.
  const [poll, setPoll] = useState(0)
  useEffect(() => {
    if (!importId || lost || (job && !inFlight(job.status))) return
    let live = true
    const timer = setTimeout(() => {
      api.slideImport(importId).then(
        (next) => {
          if (!live) return
          setJob(next)
          setError(null)
          const { percent } = next
          if (percent !== undefined) setBar((b) => Math.max(b ?? 0, percent))
        },
        (e: unknown) => {
          if (!live) return
          if (jobUnreadable(e)) {
            setError(null)
            setLost(true)
            return
          }
          // The job runs on server-side: keep reading it, or the dialog never learns that it ended.
          // A failure the app already handles is not repeated here (the backend out of reach, a sign-in).
          const handled = isTransientError(e) || (e instanceof Error && e.message === 'unauthenticated')
          if (!handled) setError(say(e, 'retry'))
          setPoll((n) => n + 1)
        },
      )
    }, pollMs)
    return () => {
      live = false
      clearTimeout(timer)
    }
    // `say` reads only `t`; a new language mid-job does not need a read of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, job, poll, pollMs, lost])

  const doneRef = useRef(false)
  useEffect(() => {
    if (job?.status !== 'done' || doneRef.current) return
    doneRef.current = true
    // Keeping the source formatting may have written a template of the bid (created, or a new
    // version): the library lists and the deck directories read it again, so the slides draw on
    // their own master without a reload.
    if (job.mode === 'source') void refreshOfficeTemplates(queryClient, bid)
    void queryClient.invalidateQueries({ queryKey: bidJobsKey(bid) })
    onJobEnd?.(job)
    // Nothing to offer: a picture slide is its own original.
    if (!isEditableMode(job.mode)) onDone(job)
  }, [job, onJobEnd, onDone, queryClient, bid])

  /** An editable import done: the dialog stays, with what it offers, until the member leaves it. */
  const ended = job?.status === 'done' && isEditableMode(job.mode) ? job : null
  const endedShown = !!ended
  const keptWords = ended?.template ? keptTemplateWords(ended.template) : null
  // Focus was on something the end took away ("Retry"): back onto the deck's heading.
  useEffect(() => {
    if (endedShown && !dialogRef.current?.contains(document.activeElement)) stepRef.current?.focus()
  }, [endedShown])
  /** Ask the editor for that panel, then leave as Close does. */
  const openPanel = (panel: ImportPanel): void => {
    if (!ended) return
    setPendingReport(ended.room, ended.id, panel)
    onDone(ended)
  }

  const retry = (): void => {
    if (!importId || !job || retrying) return
    setRetrying(true)
    setError(null)
    /** A run starts over on what is left: nothing of the last one stays on screen. */
    const restart = (status: SlideImportJob['status']): void => {
      setBar(null)
      setJob({ ...job, status, error: undefined, progress: undefined, percent: undefined })
    }
    api.retrySlideImport(importId).then(
      () => {
        if (!mountedRef.current) return
        setRetrying(false)
        // What the run does first: a picture import renders unless its renders are kept; a converted
        // one writes its slides unless they are written (then it renders the originals).
        restart(job.mode === 'pictures' ? (job.renders === 'ok' ? 'writing' : 'rendering') : job.slides.length > 0 ? 'rendering' : 'writing')
      },
      (e: unknown) => {
        if (!mountedRef.current) return
        setRetrying(false)
        // Done meanwhile (another tab's retry): read it, and the dialog ends on it.
        if (isApiError(e) && e.code === 'already_imported') {
          setBar(null)
          setJob(null)
        } else if (isApiError(e) && e.code === 'import_running') restart('rendering')
        else setError(say(e, 'retry'))
      },
    )
  }

  const close = (): void => {
    if (busyRef.current) return
    if (ended) onDone(ended)
    else onClose()
  }
  /** Escape closes; Tab / Shift+Tab stay inside the dialog. */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      return
    }
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusable = [
      ...dialogRef.current.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ].filter((el) => !(el as HTMLButtonElement).disabled && !el.hidden && el.getAttribute('type') !== 'file')
    if (focusable.length === 0) return
    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const settled = job && !inFlight(job.status) && job.status !== 'done' ? job : null
  // A failed or locked run may have placed its masters already: the lists show the template it names.
  const placedTemplate = settled?.template?.id
  useEffect(() => {
    if (placedTemplate) void refreshOfficeTemplates(queryClient, bid)
  }, [placedTemplate, queryClient, bid])
  const at = job?.progress
  // The step the backend names; an older backend names none. Pictures: rendered, then written.
  // Converted: written first, then the originals are rendered.
  const progress = at
    ? t(stepWords(mode, at.step))
    : mode === 'pictures'
      ? t(!job || job.status === 'rendering' ? 'pptx_rendering' : 'pptx_writing')
      : t(!job || job.status === 'writing' ? 'pptx_converting' : 'pptx_rendering_originals')
  const counted = at && at.total > 0 ? at : null
  const barWords =
    bar === null
      ? {}
      : {
          value: bar,
          max: 100,
          'aria-valuetext': counted
            ? fill(t('pptx_bar_counted'), { vars: { done: counted.done, total: counted.total, percent: bar } })
            : fill(t('pptx_bar_held'), { vars: { percent: bar } }),
        }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={close}>
      <div
        ref={dialogRef}
        className="modal pptx-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        aria-describedby={intro ? `${ids}-intro` : undefined}
        aria-busy={starting || staging !== null || undefined}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        // dragleave bubbles from the children too: only leaving the dialog itself ends the highlight.
        onDragOver={(e) => {
          if (!dragsFiles(e)) return
          e.preventDefault()
          if (phase === 'source') setDropping(true)
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
        }}
        onDrop={(e) => {
          setDropping(false)
          if (!dragsFiles(e)) return
          e.preventDefault()
          if (phase === 'source') takeFile(e.dataTransfer.files)
        }}
      >
        <div className="modal-head">
          <h2 id={`${ids}-title`}>{t('pptx_title')}</h2>
          <button type="button" className="modal-close" aria-label={t('close')} onClick={close} disabled={starting}>
            ✕
          </button>
        </div>
        <div className="modal-body pptx-body">
          {intro && (
            <p id={`${ids}-intro`} className="pptx-intro">
              {intro}
            </p>
          )}

          {phase === 'source' && (
            <>
              {/* The drop zone (the whole dialog takes the drop, this shows where) AND the file
                  picker. Only the button opens the picker — a clickable div would be a control with
                  no role and no keyboard path to it. */}
              <div className={'img-drop pptx-drop' + (dropping ? ' on' : '')}>
                <span className="pptx-drop-ico" aria-hidden="true">
                  <ArtefactTypeGlyph type="slides" />
                </span>
                <p className="img-drop-lead">{t('pptx_drop_lead')}</p>
                <p className="img-drop-or">{t('img_drop_or')}</p>
                <button ref={pickRef} type="button" className="mini ghost" onClick={() => fileRef.current?.click()}>
                  {t('img_upload')}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept={PPTX_ACCEPT}
                  hidden
                  onChange={(e) => {
                    takeFile(e.target.files)
                    e.target.value = '' // allow re-picking the same file
                  }}
                />
              </div>

              <div className="pptx-sp">
                {bidSite ? (
                  <button
                    type="button"
                    className="mini ghost pptx-sp-toggle"
                    aria-expanded={browsing}
                    aria-controls={browsing ? `${ids}-sp` : undefined}
                    onClick={() => setBrowsing((b) => !b)}
                  >
                    {t('pptx_sp_browse')}
                  </button>
                ) : (
                  site.isSuccess && <p className="pptx-note">{t('pptx_sp_none')}</p>
                )}
                {browsing && bidSite && (
                  <div id={`${ids}-sp`} className="pptx-sp-pane" style={{ height: spbHeightForRows(BROWSER_ROWS) }}>
                    <SharePointBrowser
                      bid={bid}
                      mode="browse"
                      start={bidSite}
                      upRow
                      pickFileTitle={t('pptx_sp_pick')}
                      onPickFile={(entry, driveId) => stage({ sharepoint: { driveId, itemId: entry.id } }, entry.name)}
                    />
                  </div>
                )}
              </div>

              <div className="pptx-link">
                <label className="prompt-label" htmlFor={`${ids}-link`}>
                  {t('pptx_link_label')}
                </label>
                <div className="pptx-link-row">
                  <input
                    id={`${ids}-link`}
                    className="prompt-input"
                    type="url"
                    value={link}
                    placeholder={t('pptx_link_ph')}
                    onChange={(e) => setLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        openLink()
                      }
                    }}
                  />
                  <button type="button" className="mini no" disabled={!link.trim()} onClick={openLink}>
                    {t('pptx_link_open')}
                  </button>
                </div>
              </div>
            </>
          )}

          {phase === 'staging' && (
            <p className="pptx-progress" role="status">
              <span className="brand-spinner" aria-hidden="true" />
              {fill(t('pptx_staging'), { vars: { name: staging ?? '' } })}
            </p>
          )}

          {phase === 'pick' && deck && (
            <>
              <h3 ref={stepRef} tabIndex={-1} className="pptx-deck">
                {deck.name}
              </h3>
              {warning && (
                <p className="pptx-warn" role="note">
                  {fill(t(warning.key), warning)}
                </p>
              )}
              <fieldset className="pptx-mode" disabled={starting}>
                <legend className="prompt-label">{t('pptx_mode_legend')}</legend>
                {DIALOG_MODES.map((m) => {
                  // A mode this deck cannot take (keeping the source formatting of a 4:3 deck): disabled, saying why.
                  const why = modeUnavailable(deck, m, templateCount)
                  return (
                    <div key={m} className="pptx-mode-row">
                      <label className="pptx-mode-opt">
                        <input
                          type="radio"
                          name={`${ids}-mode`}
                          value={m}
                          checked={mode === m}
                          disabled={!!why}
                          aria-describedby={`${ids}-mode-${m}`}
                          onChange={() => chooseMode(m)}
                        />
                        {t(MODE_WORDS[m].label)}
                      </label>
                      <p id={`${ids}-mode-${m}`} className="prompt-choice-hint">
                        {fill(t(why ?? MODE_WORDS[m].hint), { vars: { max: MAX_LIBRARY_TEMPLATES } })}
                      </p>
                      {m === 'template' && mode === 'template' && (landing || choicesFailed) && (
                        <div className="pptx-target">
                          {landing ? (
                            <TemplateMasterSelects
                              label={t('pptx_target_template')}
                              value={landing.templateId}
                              options={templateOptions}
                              masters={landingMasters}
                              master={landing.master}
                              onTemplate={(id) => {
                                setPickedTemplate(id)
                                setPickedMaster(null)
                                setError(null)
                              }}
                              onMaster={(index) => {
                                setPickedMaster(index)
                                setError(null)
                              }}
                            />
                          ) : (
                            <p className="pptx-note">{t('pptx_target_failed')}</p>
                          )}
                        </div>
                      )}
                      {m === 'source_new' && mode === 'source_new' && (
                        <div className="pptx-target">
                          <label className="prompt-label">
                            {t('pptx_source_name')}
                            <input className="prompt-input" type="text" value={newName} maxLength={MAX_TEMPLATE_NAME} onChange={(e) => setTypedName(e.target.value)} />
                          </label>
                        </div>
                      )}
                      {m === 'source_merge' && mode === 'source_merge' && (landing || choicesFailed) && (
                        <div className="pptx-target">
                          {landing ? (
                            <TemplateMasterSelects
                              label={t('pptx_target_template')}
                              value={landing.templateId}
                              options={templateOptions}
                              masters={[]}
                              master={0}
                              onTemplate={(id) => {
                                setPickedTemplate(id)
                                setPickedMaster(null)
                                setError(null)
                              }}
                              onMaster={() => {}}
                            />
                          ) : (
                            <p className="pptx-note">{t('pptx_merge_failed')}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </fieldset>
              {deck.slides.length > 0 && (
                <fieldset className="pptx-slides" disabled={starting}>
                  <legend className="prompt-label">{t('pptx_slides_legend')}</legend>
                  <div className="pptx-slides-bar">
                    <button type="button" className="mini no" disabled={pickable.length === 0} onClick={() => setSelected(new Set(pickable))}>
                      {t('pptx_all')}
                    </button>
                    <button type="button" className="mini no" onClick={() => setSelected(new Set())}>
                      {t('pptx_none')}
                    </button>
                    <span className={'pptx-count' + (extra ? ' over' : '')} role="status">
                      {fill(t('pptx_count'), { vars: { n: selected.size, max: MAX_IMPORT_SLIDES } })}
                    </span>
                  </div>
                  {isEditableMode(mode) && hasHiddenSlides(deck) && (
                    <label className="pptx-hidden-opt">
                      <input
                        type="checkbox"
                        checked={includeHidden}
                        aria-describedby={includeHidden ? `${ids}-hidden-opt` : undefined}
                        onChange={(e) => toggleHidden(e.target.checked)}
                      />
                      {t('pptx_include_hidden')}
                    </label>
                  )}
                  <ul className="pptx-list">
                    {deck.slides.map((s) => {
                      const shut = s.hidden && !hiddenOn
                      return (
                        <li key={s.index} className={s.hidden ? 'is-hidden' : undefined}>
                          <label title={shut ? t(mode === 'pictures' ? 'pptx_hidden_hint' : 'pptx_hidden_hint_editable') : undefined}>
                            <input
                              type="checkbox"
                              checked={!shut && selected.has(s.index)}
                              disabled={shut}
                              aria-describedby={shut ? `${ids}-hidden` : undefined}
                              onChange={() => toggle(s.index)}
                            />
                            <span className="pptx-num">{s.index}.</span>
                            <span className="pptx-name">{s.title.trim() || t('pptx_untitled')}</span>
                            {s.hidden && <span className="pptx-tag">{t('pptx_hidden')}</span>}
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                  {hasHiddenSlides(deck) &&
                    (hiddenOn ? (
                      <p id={`${ids}-hidden-opt`} className="pptx-note">
                        {t('pptx_hidden_no_original')}
                      </p>
                    ) : (
                      <p id={`${ids}-hidden`} className="pptx-note">
                        {t(mode === 'pictures' ? 'pptx_hidden_hint' : 'pptx_hidden_hint_editable')}
                      </p>
                    ))}
                  {pickable.length === 0 && (
                    <p className="prompt-error" role="alert">
                      {t('pptx_err_no_slides')}
                    </p>
                  )}
                  {extra > 0 && (
                    <p className="prompt-error" role="alert">
                      {fill(t('pptx_too_many'), { vars: { max: MAX_IMPORT_SLIDES, extra } })}
                    </p>
                  )}
                </fieldset>
              )}
            </>
          )}

          {phase === 'job' && (
            <>
              <h3 ref={stepRef} tabIndex={-1} className="pptx-deck">
                {deck?.name}
              </h3>
              {lost ? (
                <div className="pptx-failed" role="alert">
                  <p>{t('pptx_job_lost')}</p>
                </div>
              ) : settled ? (
                <>
                  <div className="pptx-failed" role="alert">
                    <p>
                      {t(settled.status === 'locked' ? 'pptx_locked' : settled.renders === 'failed' ? 'pptx_renders_failed' : 'pptx_failed')}
                    </p>
                    {settled.error && <p className="pptx-detail">{settled.error}</p>}
                  </div>
                  {settled.template && <KeptTemplateLine bid={bid} template={settled.template} onOpen={close} />}
                </>
              ) : ended ? (
                <div className="pptx-done">
                  <p className="pptx-done-status">{t('jobs_status_completed')}</p>
                  {ended.template && <KeptTemplateLine bid={bid} template={ended.template} onOpen={close} />}
                  {ended.renders === 'failed' && <p className="jobs-note">{t('jobs_note_renders_failed')}</p>}
                  {(ended.cutOutsPending ?? 0) > 0 && (
                    <p className="pptx-note">{fill(t('sl_import_report_waiting'), { vars: { n: ended.cutOutsPending ?? 0 } })}</p>
                  )}
                  <ImportPanelButtons job={ended} onOpen={openPanel} />
                </div>
              ) : (
                <>
                  {/* The live region announces each step, never each count: the count stays out of it. */}
                  <p className="pptx-progress">
                    <span className="brand-spinner" aria-hidden="true" />
                    <span role="status">{progress}</span>
                    {counted && (
                      <span className="pptx-progress-count">
                        {fill(t('pptx_progress_count'), { vars: { done: counted.done, total: counted.total } })}
                      </span>
                    )}
                  </p>
                  <progress className="pptx-bar" aria-label={progress} {...barWords} />
                  <p className="pptx-note">{t('pptx_close_note')}</p>
                </>
              )}
            </>
          )}

          {error && (
            <p className="prompt-error" role="alert">
              {error}
            </p>
          )}

          <div className="prompt-acts">
            {phase === 'pick' && (
              <button type="button" className="mini ghost pptx-back" onClick={otherDeck} disabled={starting}>
                {t('pptx_other_deck')}
              </button>
            )}
            <button type="button" className="mini ghost" onClick={close} disabled={starting}>
              {t(phase === 'job' ? 'close' : 'cancel')}
            </button>
            {phase === 'pick' && (
              <button type="button" className="mini ok prompt-confirm" onClick={start} disabled={!canImport}>
                {starting && <span className="brand-spinner" aria-hidden="true" />}
                {t('pptx_import')}
              </button>
            )}
            {settled && (
              <button type="button" className="mini ok prompt-confirm" onClick={retry} disabled={retrying}>
                {retrying && <span className="brand-spinner" aria-hidden="true" />}
                {t(settled.status === 'failed' && settled.renders === 'failed' ? 'pptx_retry_renders' : 'pptx_retry')}
              </button>
            )}
          </div>
          <p className="visually-hidden" role="status">
            {starting ? t('pptx_starting') : ended ? `${t('jobs_status_completed')}${keptWords ? ` ${fill(t(keptWords.key), keptWords)}` : ''}` : ''}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/**
 * New artefact › From PowerPoint: the import dialog whose target is a slides artefact created when
 * the member confirms the import, then the import into its first part — two calls, one capability
 * per endpoint (CLAUDE.md rule 8). Nothing is created if the member cancels before. The deck's
 * slides go first (`at: 0`), ahead of the starter slide every new deck is seeded with, so the part
 * opens on the first imported slide.
 */
export function NewDeckFromPowerPoint({
  bid,
  title,
  folderId = null,
  onCreated,
  onJobEnd,
  onDone,
  onClose,
}: {
  bid: string
  /** The new artefact's name. */
  title: string
  /** The explorer folder it lands in (null = the root level). */
  folderId?: string | null
  /** The artefact exists (the import may still fail): the explorer can list it. */
  onCreated?: () => void
  /** The import is done, the dialog still open or not: the explorer can read it again. */
  onJobEnd?: () => void
  /** The dialog ends on the import done: open the new part. */
  onDone: (room: string) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const target = async (): Promise<ImportTarget> => {
    const res = await api.createArtefact(bid, title, folderId, 'slides')
    if (!res.room) throw new Error('The new artefact has no part to import into.')
    onCreated?.()
    return { room: res.room, at: 0 }
  }
  return (
    <ImportPowerPointDialog
      bid={bid}
      target={target}
      intro={fill(t('pptx_into_new'), { vars: { name: title } })}
      {...(onJobEnd ? { onJobEnd } : {})}
      onDone={(job) => onDone(job.room)}
      onClose={onClose}
    />
  )
}

/** A dictionary string with its `{slots}` filled in (a function value keeps `$` literal). */
function fill(s: string, words: Pick<Words, 'vars'>): string {
  return Object.entries(words.vars ?? {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, () => String(v)), s)
}
